/**
 * Brutalist Theme Styles
 * 
 * Estilos específicos para o tema Brutalista.
 * Características: bordas grossas, sem border-radius, tipografia bold, cores sólidas.
 */

import { CSSProperties } from 'react';

// ============================================
// BASE STYLES
// ============================================

export const brutalistBase: CSSProperties = {
  background: 'var(--brut-bg)',
  color: 'var(--brut-text)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  fontFamily: 'var(--brut-font)',
  fontWeight: 'var(--brut-font-weight)' as unknown as number,
  letterSpacing: 'var(--brut-letter-spacing)',
  transition: 'var(--brut-transition)',
};

// ============================================
// INPUT STYLES
// ============================================

export const brutalistInput: CSSProperties = {
  background: 'var(--brut-bg)',
  color: 'var(--brut-text)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  padding: '12px 16px',
  fontSize: '14px',
  fontFamily: 'var(--brut-font)',
  fontWeight: 'var(--brut-font-weight-normal)' as unknown as number,
  boxShadow: 'var(--brut-shadow-offset)',
  transition: 'var(--brut-transition)',
  outline: 'none',
};

export const brutalistInputFocus: CSSProperties = {
  boxShadow: 'var(--brut-shadow-offset-lg)',
  transform: 'var(--brut-hover-offset)',
  borderColor: 'var(--brut-accent)',
};

export const brutalistInputError: CSSProperties = {
  borderColor: 'var(--brut-accent)',
  boxShadow: 'var(--brut-shadow-offset-accent)',
};

export const brutalistInputDisabled: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  boxShadow: 'none',
};

// ============================================
// BUTTON STYLES
// ============================================

export const brutalistButton: CSSProperties = {
  background: 'var(--brut-bg)',
  color: 'var(--brut-text)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  padding: '12px 24px',
  fontSize: '14px',
  fontFamily: 'var(--brut-font)',
  fontWeight: 'var(--brut-font-weight)' as unknown as number,
  textTransform: 'uppercase',
  letterSpacing: 'var(--brut-letter-spacing)',
  boxShadow: 'var(--brut-shadow-offset)',
  cursor: 'pointer',
  transition: 'var(--brut-transition)',
  outline: 'none',
};

export const brutalistButtonHover: CSSProperties = {
  transform: 'var(--brut-hover-offset)',
  boxShadow: 'var(--brut-hover-shadow)',
};

export const brutalistButtonActive: CSSProperties = {
  transform: 'var(--brut-active-offset)',
  boxShadow: 'var(--brut-active-shadow)',
};

export const brutalistButtonPrimary: CSSProperties = {
  background: 'var(--brut-text)',
  color: 'var(--brut-bg)',
  border: 'var(--brut-border)',
};

export const brutalistButtonDanger: CSSProperties = {
  background: 'var(--brut-accent)',
  color: 'var(--brut-bg)',
  border: 'var(--brut-border-accent)',
  boxShadow: 'var(--brut-shadow-offset)',
};

export const brutalistButtonDisabled: CSSProperties = {
  opacity: 0.5,
  cursor: 'not-allowed',
  boxShadow: 'none',
  transform: 'none',
};

// ============================================
// CARD STYLES
// ============================================

export const brutalistCard: CSSProperties = {
  background: 'var(--brut-bg)',
  color: 'var(--brut-text)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  padding: 'var(--brut-spacing-lg)',
  boxShadow: 'var(--brut-shadow-offset)',
  transition: 'var(--brut-transition)',
};

export const brutalistCardHover: CSSProperties = {
  transform: 'var(--brut-hover-offset)',
  boxShadow: 'var(--brut-hover-shadow)',
};

export const brutalistCardHeader: CSSProperties = {
  borderBottom: 'var(--brut-border)',
  paddingBottom: 'var(--brut-spacing-md)',
  marginBottom: 'var(--brut-spacing-md)',
  fontWeight: 'var(--brut-font-weight)' as unknown as number,
  textTransform: 'uppercase',
};

// ============================================
// SELECT STYLES
// ============================================

export const brutalistSelect: CSSProperties = {
  background: 'var(--brut-bg)',
  color: 'var(--brut-text)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  padding: '12px 40px 12px 16px',
  fontSize: '14px',
  fontFamily: 'var(--brut-font)',
  fontWeight: 'var(--brut-font-weight-normal)' as unknown as number,
  boxShadow: 'var(--brut-shadow-offset)',
  cursor: 'pointer',
  transition: 'var(--brut-transition)',
  appearance: 'none',
};

export const brutalistSelectDropdown: CSSProperties = {
  background: 'var(--brut-bg)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  boxShadow: 'var(--brut-shadow-offset-lg)',
  marginTop: '4px',
};

export const brutalistSelectOption: CSSProperties = {
  padding: '12px 16px',
  cursor: 'pointer',
  borderBottom: '2px solid var(--brut-text)',
  transition: 'var(--brut-transition)',
  fontWeight: 'var(--brut-font-weight-normal)' as unknown as number,
};

export const brutalistSelectOptionHover: CSSProperties = {
  background: 'var(--brut-text)',
  color: 'var(--brut-bg)',
};

export const brutalistSelectOptionLast: CSSProperties = {
  borderBottom: 'none',
};

// ============================================
// TABLE STYLES
// ============================================

export const brutalistTable: CSSProperties = {
  background: 'var(--brut-bg)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  boxShadow: 'var(--brut-shadow-offset)',
  overflow: 'hidden',
  borderCollapse: 'collapse',
  width: '100%',
};

export const brutalistTableHeader: CSSProperties = {
  background: 'var(--brut-text)',
  color: 'var(--brut-bg)',
  padding: '16px',
  fontWeight: 'var(--brut-font-weight)' as unknown as number,
  textTransform: 'uppercase',
  letterSpacing: 'var(--brut-letter-spacing)',
  borderBottom: 'var(--brut-border)',
  textAlign: 'left',
};

export const brutalistTableRow: CSSProperties = {
  borderBottom: '2px solid var(--brut-text)',
  transition: 'var(--brut-transition)',
};

export const brutalistTableRowHover: CSSProperties = {
  background: 'var(--brut-bg-secondary)',
};

export const brutalistTableCell: CSSProperties = {
  padding: '14px 16px',
  color: 'var(--brut-text)',
  borderRight: '2px solid var(--brut-text)',
};

export const brutalistTableCellLast: CSSProperties = {
  borderRight: 'none',
};

// ============================================
// MODAL STYLES
// ============================================

export const brutalistModalOverlay: CSSProperties = {
  background: 'rgba(0, 0, 0, 0.8)',
};

export const brutalistModal: CSSProperties = {
  background: 'var(--brut-bg)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  boxShadow: 'var(--brut-shadow-offset-lg)',
  padding: 'var(--brut-spacing-xl)',
};

export const brutalistModalHeader: CSSProperties = {
  borderBottom: 'var(--brut-border)',
  paddingBottom: 'var(--brut-spacing-md)',
  marginBottom: 'var(--brut-spacing-lg)',
  fontWeight: 'var(--brut-font-weight)' as unknown as number,
  textTransform: 'uppercase',
  letterSpacing: 'var(--brut-letter-spacing)',
};

export const brutalistModalCloseButton: CSSProperties = {
  background: 'transparent',
  border: 'var(--brut-border)',
  padding: '8px 12px',
  cursor: 'pointer',
  fontWeight: 'var(--brut-font-weight)' as unknown as number,
  fontSize: '18px',
};

// ============================================
// TABS STYLES
// ============================================

export const brutalistTabs: CSSProperties = {
  display: 'flex',
  borderBottom: 'var(--brut-border)',
  gap: '0',
};

export const brutalistTab: CSSProperties = {
  padding: '12px 24px',
  background: 'var(--brut-bg)',
  color: 'var(--brut-text)',
  border: 'var(--brut-border)',
  borderBottom: 'none',
  borderRadius: 'var(--brut-radius)',
  cursor: 'pointer',
  fontFamily: 'var(--brut-font)',
  fontWeight: 'var(--brut-font-weight-normal)' as unknown as number,
  textTransform: 'uppercase',
  letterSpacing: 'var(--brut-letter-spacing)',
  transition: 'var(--brut-transition)',
  marginRight: '-4px',
};

export const brutalistTabActive: CSSProperties = {
  background: 'var(--brut-text)',
  color: 'var(--brut-bg)',
  fontWeight: 'var(--brut-font-weight)' as unknown as number,
  position: 'relative',
  zIndex: 1,
};

export const brutalistTabHover: CSSProperties = {
  background: 'var(--brut-bg-secondary)',
};

// ============================================
// BADGE STYLES
// ============================================

export const brutalistBadge: CSSProperties = {
  background: 'var(--brut-bg)',
  color: 'var(--brut-text)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  padding: '4px 12px',
  fontSize: '12px',
  fontFamily: 'var(--brut-font)',
  fontWeight: 'var(--brut-font-weight)' as unknown as number,
  textTransform: 'uppercase',
  letterSpacing: 'var(--brut-letter-spacing)',
  boxShadow: 'var(--brut-shadow-offset-sm)',
};

export const brutalistBadgeSuccess: CSSProperties = {
  background: 'var(--brut-accent-green)',
  color: 'var(--brut-text)',
};

export const brutalistBadgeWarning: CSSProperties = {
  background: 'var(--brut-accent-yellow)',
  color: 'var(--brut-text)',
};

export const brutalistBadgeDanger: CSSProperties = {
  background: 'var(--brut-accent)',
  color: 'var(--brut-bg)',
};

export const brutalistBadgeInfo: CSSProperties = {
  background: 'var(--brut-accent-blue)',
  color: 'var(--brut-bg)',
};

// ============================================
// TOOLTIP STYLES
// ============================================

export const brutalistTooltip: CSSProperties = {
  background: 'var(--brut-text)',
  color: 'var(--brut-bg)',
  border: 'var(--brut-border)',
  borderRadius: 'var(--brut-radius)',
  padding: '8px 12px',
  fontSize: '12px',
  fontFamily: 'var(--brut-font)',
  fontWeight: 'var(--brut-font-weight-normal)' as unknown as number,
  boxShadow: 'var(--brut-shadow-offset-sm)',
  textTransform: 'uppercase',
};

export const brutalistTooltipArrow: CSSProperties = {
  borderColor: 'var(--brut-text)',
};

